/**
 * NoteOrganizersDialog — CRUD for note-only organizers on a game.
 *
 * Two-level nesting: Organizer → Subgroup. Notes reference these via the
 * `noteOrganizerId` and `noteSubgroupId` fields on the note doc itself
 * (see `routes/games.py::NoteCreateRequest`).
 *
 * Independent from the achievement `sections` collection — a game can have
 * completely different organization schemes for achievements vs notes.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FolderPlus, Folder, FolderOpen, ChevronRight, ChevronDown,
  Trash2, Plus, Save, X, Edit2, Loader2, StickyNote,
} from "lucide-react";

export function NoteOrganizersDialog({ open, onOpenChange, gameId, api, onChanged }) {
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [editingOrgName, setEditingOrgName] = useState("");
  const [editingSub, setEditingSub] = useState(null); // { orgId, subId, name }
  const [newSubName, setNewSubName] = useState({}); // { [orgId]: name }

  const fetchOrganizers = async () => {
    if (!gameId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${api}/games/${gameId}/note-organizers`);
      setOrganizers(res.data || []);
    } catch (e) {
      toast.error("Failed to load note organizers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchOrganizers();
  }, [open, gameId]);

  const toggleExpanded = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const createOrganizer = async () => {
    const name = newOrgName.trim();
    if (!name) return;
    try {
      setCreatingOrg(true);
      await axios.post(`${api}/games/${gameId}/note-organizers`, { name });
      setNewOrgName("");
      await fetchOrganizers();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed to create organizer");
    } finally {
      setCreatingOrg(false);
    }
  };

  const renameOrganizer = async (orgId) => {
    const name = editingOrgName.trim();
    if (!name) { setEditingOrgId(null); return; }
    try {
      await axios.put(`${api}/games/${gameId}/note-organizers/${orgId}`, { name });
      setEditingOrgId(null);
      await fetchOrganizers();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed to rename");
    }
  };

  const deleteOrganizer = async (org) => {
    if (!window.confirm(`Delete organizer "${org.name}"? Notes filed inside will move to Uncategorized (they won't be deleted).`)) return;
    try {
      await axios.delete(`${api}/games/${gameId}/note-organizers/${org.id}`);
      await fetchOrganizers();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const addSubgroup = async (orgId) => {
    const name = (newSubName[orgId] || "").trim();
    if (!name) return;
    try {
      await axios.post(`${api}/games/${gameId}/note-organizers/${orgId}/subgroups`, { name });
      setNewSubName(prev => ({ ...prev, [orgId]: "" }));
      await fetchOrganizers();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed to add subgroup");
    }
  };

  const renameSubgroup = async () => {
    if (!editingSub) return;
    const name = (editingSub.name || "").trim();
    if (!name) { setEditingSub(null); return; }
    try {
      await axios.put(
        `${api}/games/${gameId}/note-organizers/${editingSub.orgId}/subgroups/${editingSub.subId}`,
        { name }
      );
      setEditingSub(null);
      await fetchOrganizers();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed to rename subgroup");
    }
  };

  const deleteSubgroup = async (orgId, sub) => {
    if (!window.confirm(`Delete subgroup "${sub.name}"? Notes inside will move up to the parent organizer.`)) return;
    try {
      await axios.delete(`${api}/games/${gameId}/note-organizers/${orgId}/subgroups/${sub.id}`);
      await fetchOrganizers();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed to delete subgroup");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="note-organizers-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-amber-400" />
            Manage Note Organizers
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Group notes by subject so long notebooks stay easy to browse. Each
            organizer can contain up to one level of subgroups. Deleting an
            organizer never deletes the notes inside — they move to
            Uncategorized.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pt-2">
          {/* New organizer input */}
          <div className="flex items-center gap-2 p-2 rounded border border-white/10 bg-black/30">
            <Input
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createOrganizer()}
              placeholder="New organizer name (e.g., Walkthroughs)"
              className="bg-black/50 border-white/10 h-8 text-sm"
              data-testid="new-organizer-name"
            />
            <Button
              size="sm"
              onClick={createOrganizer}
              disabled={creatingOrg || !newOrgName.trim()}
              className="bg-amber-600 hover:bg-amber-500 h-8 text-xs"
              data-testid="create-organizer-btn"
            >
              {creatingOrg ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FolderPlus className="w-3 h-3 mr-1" />}
              Add
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8 text-white/50">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : organizers.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-6">
              No organizers yet. Add one above to start grouping your notes.
            </p>
          ) : (
            <div className="space-y-1.5">
              {organizers.map(org => {
                const expanded = expandedIds.has(org.id);
                const isEditingThis = editingOrgId === org.id;
                return (
                  <div key={org.id} className="rounded border border-white/10 bg-black/40" data-testid={`organizer-row-${org.id}`}>
                    <div className="flex items-center gap-1.5 p-2">
                      <button
                        onClick={() => toggleExpanded(org.id)}
                        className="text-white/40 hover:text-white shrink-0"
                        data-testid={`organizer-toggle-${org.id}`}
                      >
                        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expanded ? <FolderOpen className="w-4 h-4 text-amber-400 shrink-0" /> : <Folder className="w-4 h-4 text-amber-400 shrink-0" />}
                      {isEditingThis ? (
                        <Input
                          value={editingOrgName}
                          onChange={(e) => setEditingOrgName(e.target.value)}
                          onBlur={() => renameOrganizer(org.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") renameOrganizer(org.id);
                            if (e.key === "Escape") setEditingOrgId(null);
                          }}
                          autoFocus
                          className="bg-black/50 border-white/10 h-7 text-sm flex-1"
                          data-testid={`organizer-name-input-${org.id}`}
                        />
                      ) : (
                        <button
                          onClick={() => { setEditingOrgId(org.id); setEditingOrgName(org.name); }}
                          className="text-sm text-white flex-1 text-left hover:text-amber-300 truncate"
                          title="Click to rename"
                        >
                          {org.name}
                        </button>
                      )}
                      <span className="text-[10px] text-white/40">
                        {(org.subgroups || []).length} subgroup{(org.subgroups || []).length === 1 ? "" : "s"}
                      </span>
                      <button
                        onClick={() => deleteOrganizer(org)}
                        className="text-white/30 hover:text-red-400 shrink-0"
                        title="Delete organizer"
                        data-testid={`delete-organizer-${org.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {expanded && (
                      <div className="px-3 pb-2 pl-9 space-y-1.5">
                        {(org.subgroups || []).map(sub => (
                          <div
                            key={sub.id}
                            className="flex items-center gap-1.5 p-1.5 rounded border border-white/5 bg-black/30"
                            data-testid={`subgroup-row-${sub.id}`}
                          >
                            <Folder className="w-3.5 h-3.5 text-amber-300/60 shrink-0" />
                            {editingSub?.subId === sub.id ? (
                              <Input
                                value={editingSub.name}
                                onChange={(e) => setEditingSub({ ...editingSub, name: e.target.value })}
                                onBlur={renameSubgroup}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") renameSubgroup();
                                  if (e.key === "Escape") setEditingSub(null);
                                }}
                                autoFocus
                                className="bg-black/50 border-white/10 h-6 text-xs flex-1"
                              />
                            ) : (
                              <button
                                onClick={() => setEditingSub({ orgId: org.id, subId: sub.id, name: sub.name })}
                                className="text-xs text-white/80 flex-1 text-left hover:text-amber-300 truncate"
                                title="Click to rename"
                              >
                                {sub.name}
                              </button>
                            )}
                            <button
                              onClick={() => deleteSubgroup(org.id, sub)}
                              className="text-white/30 hover:text-red-400 shrink-0"
                              title="Delete subgroup"
                              data-testid={`delete-subgroup-${sub.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center gap-1.5">
                          <Input
                            value={newSubName[org.id] || ""}
                            onChange={(e) => setNewSubName(prev => ({ ...prev, [org.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && addSubgroup(org.id)}
                            placeholder="Add subgroup…"
                            className="bg-black/50 border-white/10 h-6 text-xs flex-1"
                            data-testid={`new-subgroup-name-${org.id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => addSubgroup(org.id)}
                            disabled={!(newSubName[org.id] || "").trim()}
                            className="bg-amber-600/60 hover:bg-amber-500 h-6 text-[10px] px-2"
                            data-testid={`add-subgroup-${org.id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end pt-3 border-t border-white/10">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70 hover:text-white">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NoteOrganizersDialog;
